/**
 * 서비스 타입 기반 권한 관리 훅
 *
 * 서비스 타입:
 * - MASTER: 모든 권한 (제한 없음) — 대표 전용
 * - MANAGER: BLB 내부 담당자. adminRole 로 AE / KOL Specialist 둘로 나뉜다.
 *     · AE  : 마스터와 거의 동일(금액·정산 전부 열람). 회사 초대코드 관리와 캠페인 삭제만 제외.
 *     · KOL : 섭외·운영 담당. **KOL 단가는 보고, 클라이언트 청구가는 못 본다**
 *             (2026-07-29 대표님 기준). 섭외를 하려면 크리에이터에게 줄 돈은 알아야 하고,
 *             우리가 얼마에 파는지는 알 필요가 없다 → 마진은 가려진다. 정산 화면도 숨김.
 *     adminRole 이 비어 있으면 보수적으로 KOL Specialist 으로 본다(금액이 새는 쪽보다 안전).
 * - DELIVERY_VIETNAM: Shipping 메뉴에서 Vietnam만 표시
 * - DELIVERY_THAILAND: Shipping 메뉴에서 Thailand만 표시
 * - CLIENT: 클라이언트 전용 제한된 권한
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { loadMatrix, refreshMatrixFromServer, subscribeMatrix, toMatrixRole } from '../config/permissionMatrix';
import { getPreviewRole, PREVIEW_ROLE_MAP } from '../config/rolePreview';

// 서비스 타입 상수
export const SERVICE_TYPES = {
  MASTER: 'MASTER',
  MANAGER: 'MANAGER',
  DELIVERY_VIETNAM: 'DELIVERY_VIETNAM',
  DELIVERY_THAILAND: 'DELIVERY_THAILAND',
  CLIENT: 'CLIENT',
  INFLUENCER: 'INFLUENCER',
};

/**
 * 현재 로그인한 사용자의 서비스 타입 가져오기
 * (저장된 값이 없으면 빈 문자열 — 어떤 권한도 갖지 못함)
 */
export const getServiceType = () => {
  return localStorage.getItem('serviceType') || '';
};

// BLB 내부 담당자 세부 역할 (serviceType=MANAGER 안에서 갈린다)
export const ADMIN_ROLES = {
  AE: 'AE',
  KOL: 'KOL',
};

/**
 * 현재 로그인한 사용자의 세부 역할(AE / KOL) 가져오기.
 * 로그인 응답의 user.adminRole 을 그대로 저장해 둔 값이다.
 */
export const getAdminRole = () => {
  return localStorage.getItem('adminRole') || '';
};

/**
 * 현재 로그인한 사용자의 클라이언트 ID 가져오기 (CLIENT 역할용)
 */
export const getClientId = () => {
  return localStorage.getItem('verifiedClientId') || localStorage.getItem('clientId') || '';
};

/**
 * 서비스 타입에 따른 기본 랜딩 페이지 경로 반환
 * (로그인 직후 리다이렉트에 사용)
 */
export const getDefaultRoute = (serviceType) => {
  const type = serviceType || getServiceType();

  if (type === SERVICE_TYPES.INFLUENCER) {
    return '/mypage';
  }
  if (type === SERVICE_TYPES.DELIVERY_VIETNAM || type === SERVICE_TYPES.DELIVERY_THAILAND) {
    return '/shipping';
  }
  if (type === SERVICE_TYPES.CLIENT) {
    return '/my-kols';
  }
  return '/dashboard';
};

/**
 * 권한 관리 훅
 * @returns {Object} 권한 관련 유틸리티 함수들
 */
export const usePermission = () => {
  // 역할 미리보기가 켜져 있으면 그 역할인 척한다(화면만. API 는 여전히 내 토큰으로 나간다).
  const previewRole = useMemo(() => getPreviewRole(), []);
  const preview = previewRole ? PREVIEW_ROLE_MAP[previewRole] : null;

  const serviceType = useMemo(
    () => (preview ? preview.serviceType : getServiceType()),
    [preview],
  );
  const userClientId = useMemo(() => getClientId(), []);

  // MASTER 권한 여부 (미리보기 중이면 그 역할 기준)
  const isMaster = useMemo(() => serviceType === SERVICE_TYPES.MASTER, [serviceType]);

  // 미리보기와 무관한 '진짜 내 권한'. 미리보기를 켠 채로도 되돌릴 수 있어야 하는
  // 버튼(역할 미리보기·회사/권한 관리)은 반드시 이 값으로 판단해야 한다.
  const isRealMaster = useMemo(() => getServiceType() === SERVICE_TYPES.MASTER, []);

  // MANAGER 권한 여부 (BLB 내부 담당자 — 아래에서 AE / KOL Specialist 으로 갈린다)
  const isManager = useMemo(() => serviceType === SERVICE_TYPES.MANAGER, [serviceType]);

  const adminRole = useMemo(
    () => (preview ? preview.adminRole : getAdminRole()),
    [preview],
  );

  // AE — 마스터와 거의 같은 권한 (금액·정산 열람 가능)
  const isAE = useMemo(
    () => isManager && adminRole === ADMIN_ROLES.AE,
    [isManager, adminRole],
  );

  // KOL Specialist — 기존 MANAGER 그대로 (금액·정산 숨김).
  // adminRole 이 아직 안 붙은 계정도 여기로 본다: 금액이 새는 쪽보다 안 보이는 쪽이 안전하다.
  const isKolTeam = useMemo(
    () => isManager && adminRole !== ADMIN_ROLES.AE,
    [isManager, adminRole],
  );

  // 금액·정산까지 다 보는 내부 상위 권한 (MASTER + AE)
  const hasFullAdminAccess = useMemo(() => isMaster || isAE, [isMaster, isAE]);

  // CLIENT 권한 여부
  const isClient = useMemo(() => serviceType === SERVICE_TYPES.CLIENT, [serviceType]);

  // INFLUENCER 권한 여부
  const isInfluencer = useMemo(() => serviceType === SERVICE_TYPES.INFLUENCER, [serviceType]);

  // ============ 권한 표(config/permissionMatrix.js)에서 읽는 값들 ============
  // 「회사·권한 관리」 화면의 [권한 관리] 탭에서 체크박스로 켜고 끄면 서버에 저장되고 여기로 들어온다.
  // 표가 바뀌면(저장·서버에서 다시 읽음) 새로고침 없이 이 훅을 쓰는 화면이 다시 그려진다.
  const matrixRole = useMemo(() => toMatrixRole(serviceType, adminRole), [serviceType, adminRole]);
  const matrix = useSyncExternalStore(subscribeMatrix, loadMatrix);
  // 화면을 열 때마다 서버 값을 확인한다(1분에 한 번 이내로 묶여 요청이 몰리지 않는다).
  useEffect(() => { refreshMatrixFromServer(); }, []);
  const can = useCallback(
    (key) => (matrixRole ? matrix[key]?.[matrixRole] === true : false),
    [matrix, matrixRole],
  );

  // Client Price 관련 컬럼 표시 여부 (기본값: MASTER·AE = 마진 관리용, CLIENT = 자기 청구 가격 확인용)
  const canViewClientPrice = can('canViewClientPrice');

  // KOL 단가·원가 컬럼 (기본값: MASTER·AE·KOL Specialist — 섭외에 필요한 값이라 켜 둔다)
  // → KOL Specialist 은 canViewClientPrice(false)와 합쳐 "KOL 단가는 보이고 마진은 안 보임".
  const canViewKolPrice = can('canViewKolPrice');

  // 캠페인 삭제 버튼 표시 여부 (기본값: MASTER만 — 되돌릴 수 없는 동작)
  const canDeleteCampaign = can('canDeleteCampaign');

  // 회사 초대코드 관리 화면 접근 여부 (기본값: MASTER만)
  const canManageClientInvites = can('canManageClientInvites');

  // 휴지통 화면 접근 여부 (기본값: MASTER·AE·KOL — 2026-10-19 KOL 추가)
  const canViewTrash = can('canViewTrash');
  // 휴지통 삭제 요청(대표가 아니면 요청 → 대표 승인, 기본값: MASTER·AE·KOL)
  const canRequestTrashPurge = can('canRequestTrashPurge');

  // 역할 미리보기 버튼 표시 여부 (기본값: MASTER만 — 우리 쪽 사람만 쓰는 도구다)
  const canPreviewRoles = can('canPreviewRoles');

  // Shipping 페이지에서 허용된 배송 국가
  // DELIVERY_THAILAND: Thailand만, DELIVERY_VIETNAM: Vietnam만, 그 외: 모든 국가
  const allowedShippingCountry = useMemo(() => {
    switch (serviceType) {
      case SERVICE_TYPES.DELIVERY_THAILAND:
        return 'TH';
      case SERVICE_TYPES.DELIVERY_VIETNAM:
        return 'VN';
      default:
        return null; // null이면 모든 국가 표시
    }
  }, [serviceType]);

  // Delivery 담당자 여부 (DELIVERY_THAILAND 또는 DELIVERY_VIETNAM)
  const isDeliveryRole = useMemo(() => {
    return serviceType === SERVICE_TYPES.DELIVERY_THAILAND ||
           serviceType === SERVICE_TYPES.DELIVERY_VIETNAM;
  }, [serviceType]);

  // Shipping 관련 (기본값: 배송사는 등록·수정 불가, 클라이언트는 등록만 가능)
  const canCreateShipping = can('canCreateShipping');
  const canEstimateShipping = can('canEstimateShipping');
  const canEditShippingDetail = can('canEditShippingDetail');
  // 배송 삭제(휴지통으로) — 2026-10-19 Junha·사용자 «휴지통에 넣는 건 AE·KOL 도». 대표가 아니면 1차 등록 전까지만(서버도 같다)
  const canDeleteShipping = can('canDeleteShipping');
  const canViewFullShippingDetail = can('canViewFullShippingDetail');

  // ============ 메뉴 접근 (기본값: INFLUENCER는 어드민 페이지 모두 차단) ============

  const canViewDashboard = can('canViewDashboard');
  const canViewKolsDB = can('canViewKolsDB');
  const canViewMyKols = can('canViewMyKols');
  const canViewCampaign = can('canViewCampaign');
  const canViewShipping = can('canViewShipping');
  const canViewDelivery = can('canViewDelivery');
  const canViewInvoice = can('canViewInvoice');

  // 정산 메뉴 — 기본값 MASTER·AE.
  // 정산 화면은 클라 청구액·KOL 단가·마진이 전부 드러나므로,
  // 금액을 못 보는 KOL Specialist과 CLIENT/DELIVERY/INFLUENCER는 메뉴 자체를 숨긴다.
  const canViewSettlement = can('canViewSettlement');

  /* 서비스 타입별 기본 랜딩 페이지 — 로그인 직후 도착하는 곳이자, 로고를 눌렀을 때 가는 곳이다.
     authHelpers 의 getDefaultRoute(로그인 직후 이동에 쓴다)와 반드시 같은 답을 내야 한다.
     예전에는 클라이언트가 달랐다 — 로그인은 My KOLs 로 보내는데 여기서는 대시보드라,
     로고를 누르면 로그인 직후와 다른 화면이 나왔다(2026-08-13 맞춤). */
  const defaultRoute = useMemo(() => {
    if (isInfluencer) {
      return '/mypage';
    }
    if (isDeliveryRole) {
      return '/shipping';
    }
    if (isClient) {
      return '/my-kols';
    }
    return '/dashboard';
  }, [isInfluencer, isDeliveryRole, isClient]);

  // ============ My KOLs · 캠페인 ============

  const canCreateMyKolsList = can('canCreateMyKolsList');
  const canCalculateClientPrice = can('canCalculateClientPrice');
  const canEditMyKols = can('canEditMyKols');
  const canAssignMyKols = can('canAssignMyKols');
  const canCreateCampaign = can('canCreateCampaign');
  const canSearchByClient = can('canSearchByClient');

  // My KOLs 페이지에서 테이블 select 컬럼 표시 여부 (CLIENT도 표시 — 클라이언트 진행 상태 변경/배정 취소용)
  const canSelectMyKolsRows = useMemo(() => true, []);

  // 캠페인 사이드바에서 대시보드 버튼 표시 여부 (CLIENT도 노출)
  const canViewCampaignDashboard = useMemo(() => true, []);

  // Shipping 상세 페이지 — 메모 섹션 / Client Note 외의 노트
  const canViewShippingMemo = can('canViewShippingMemo');
  const canViewAllNotes = can('canViewAllNotes');

  return {
    serviceType,
    adminRole,
    previewRole,
    userClientId,
    isMaster,
    isRealMaster,
    isManager,
    isAE,
    isKolTeam,
    hasFullAdminAccess,
    isClient,
    isInfluencer,
    canViewClientPrice,
    canViewKolPrice,
    canDeleteCampaign,
    canManageClientInvites,
    canPreviewRoles,
    canViewTrash,
    canRequestTrashPurge,
    matrixRole,
    allowedShippingCountry,
    isDeliveryRole,
    canCreateShipping,
    canEstimateShipping,
    canEditShippingDetail,
    canDeleteShipping,
    canViewFullShippingDetail,
    // DELIVERY 역할 전용
    canViewDashboard,
    canViewKolsDB,
    canViewMyKols,
    canViewCampaign,
    canViewShipping,
    canViewDelivery,
    canViewInvoice,
    canViewSettlement,
    defaultRoute,
    // CLIENT 역할 전용
    canCreateMyKolsList,
    canCalculateClientPrice,
    canEditMyKols,
    canAssignMyKols,
    canSelectMyKolsRows,
    canCreateCampaign,
    canViewCampaignDashboard,
    canSearchByClient,
    canViewShippingMemo,
    canViewAllNotes,
  };
};

export default usePermission;
